/**
 * useOrderViewLoadingState Composable
 * Determines when to show loading skeleton in OrderView
 * Single Responsibility: Loading state logic for OrderView
 * Compatible with Vue 2
 */

/**
 * Composable for managing loading state in OrderView
 * @param {Object} store - Vuex store instance
 * @param {Object} selectedStoreComposable - useSelectedStore composable instance
 * @returns {Object} Loading state methods
 */
export function useOrderViewLoadingState(store, selectedStoreComposable) {
  if (!store) {
    console.warn("useOrderViewLoadingState: Store instance is required");
    return {
      isLoadingStoreData: () => true
    };
  }

  /**
   * Determines if store data is still loading
   * Shows skeleton by default until we have COMPLETE information from API
   * This prevents showing content prematurely when alias is obtained from URL
   * @returns {boolean} True if should show loading skeleton
   */
  function isLoadingStoreData() {
    const fetchingData = store.getters.getFetchingData;
    const storeData = store.getters.getStore || {};
    const stores = store.getters.getStores || [];
    const categories =
      store.getters["store/getCategories"] || store.getters.categories || [];
    const products =
      store.getters["store/getProducts"] || store.getters.products || [];
    const storeName = store.getters.getStoreName;
    const storeAlias = store.getters.getStoreAlias;

    // Always show skeleton if:
    // 1. Still fetching data (API request in progress)
    // 2. Missing basic store info (name, alias, store object) - means API hasn't responded yet
    // 3. Have locations but no location selected
    // 4. Have location selected but missing essential config (API response incomplete):
    //    - No categories or products
    //    - No dining options
    //    - No payment methods
    //    - Store object is empty or incomplete

    // If fetchingData is true, always show skeleton (API request in progress)
    if (fetchingData) {
      return true;
    }

    // If we don't have basic info, show skeleton (API hasn't responded or failed)
    const missingBasicInfo =
      !storeName ||
      !storeAlias ||
      !storeData ||
      Object.keys(storeData).length === 0;
    if (missingBasicInfo) {
      return true;
    }

    // If we have locations but no selection, show skeleton
    const selectedStore = selectedStoreComposable
      ? selectedStoreComposable.getSelectedStore()
      : store.getters.selectedLocation;
    const hasLocationsButNoSelection = stores.length > 0 && !selectedStore;
    if (hasLocationsButNoSelection) {
      return true;
    }

    // If we have location but config is incomplete, show skeleton
    const hasLocationButNoConfig =
      selectedStore && (categories.length === 0 || products.length === 0);

    return hasLocationButNoConfig;
  }

  return {
    isLoadingStoreData
  };
}
